/**
 * Haptic feedback, cross-platform.
 *
 * - Android / Chromium: `navigator.vibrate` (the original path).
 * - iOS Safari + installed PWA: `navigator.vibrate` DOES NOT EXIST — every
 *   vibrate call has always been a silent no-op on iPhone. Safari 17.4+
 *   plays the native "switch toggle" haptic when a hidden
 *   `<input type="checkbox" switch>` is clicked programmatically during a
 *   user gesture — we ride that (the same trick polished PWAs use).
 * - Anything else: silent no-op (calling haptic() is always safe).
 *
 * FOOTGUN — the synthetic click travels document CAPTURE listeners: the
 * hidden element is tagged `[data-cvx-haptic]` and the outside-tap absorbers
 * (ActionMenu, UserAccountMenu) explicitly ignore that tag, else a haptic
 * fired while a menu is open would read as an outside tap and dismiss it.
 * The bulk-bar resolver already ignores body-level portals outside
 * `[data-cvx-app-push]`, and scrollTapGuard only suppresses clicks inside
 * recently-scrolled containers — the body-level input is in neither.
 */

const patterns = {
  light: 5,
  tap: 8,
  warning: [20, 60, 20],
};

let switchInput = null;

/** Lazily create the hidden switch input (singleton, body-level). */
function ensureSwitchInput() {
  if (switchInput && document.body.contains(switchInput)) return switchInput;
  try {
    const label = document.createElement('label');
    label.setAttribute('data-cvx-haptic', 'true');
    label.setAttribute('aria-hidden', 'true');
    // NOT display:none (WebKit may skip activation behavior entirely) —
    // visually hidden + inert instead.
    label.style.cssText = 'position:fixed;top:-100px;left:-100px;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    input.setAttribute('data-cvx-haptic', 'true');
    input.tabIndex = -1;
    label.appendChild(input);
    document.body.appendChild(label);
    switchInput = input;
  } catch {
    switchInput = null;
  }
  return switchInput;
}

function iosSwitchTick() {
  const input = ensureSwitchInput();
  if (!input) return;
  try {
    input.click();
  } catch {
    /* no-op */
  }
}

export function haptic(pattern) {
  if (typeof navigator === 'undefined') return;
  if ('vibrate' in navigator) {
    navigator.vibrate(patterns[pattern]);
    return;
  }
  // iOS path — switch-toggle haptic (Safari 17.4+; silent no-op elsewhere).
  if (typeof document === 'undefined' || !document.body) return;
  iosSwitchTick();
  if (pattern === 'warning') {
    // Approximate the triple-buzz warning with spaced ticks.
    setTimeout(iosSwitchTick, 70);
    setTimeout(iosSwitchTick, 140);
  }
}
